import { memo, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { cloneDeep } from "lodash";
import { invariant } from "@searchui-searchforms/shared/utils";
import {
    EVENT_DEFAULT_INSTANCE,
    Form,
    type FormValues,
    RELATION_DEFAULT_INSTANCE,
    type SubmitMeta,
} from "@/widgets/classic-form/form";
import { useOnAddField, useOnRemoveField } from "@/features/form-fields";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";
import type { FieldPath, FormAPI } from "@/shared/forms";
import "@/shared/styles/globals.scss";
import { FullFormStructure } from "./FullFormStructure";
import { IsEditFormProvider } from "./IsEditFormContext";
import { useDefaultFormValues } from "./form-control/useDefaultFormValues";
import { useOnSubmit } from "./form-control/useOnSubmit";
import { useRestoreFormHistoryState } from "./form-history";
import { MatchAllTermsProvider, type MatchAllTermsProviderRef } from "./match-all-terms";
import { useSearchAssist } from "./search-assist";
import { useResetFormOnUpstreamChange } from "./useResetFormOnUpstreamChange";
import styles from "./ClassicForm.module.scss";

const MemoizedFullFormStructure = memo(FullFormStructure);

interface ClassicFormProps {
    isEditForm?: boolean;
}

export const ClassicForm = ({ isEditForm = false }: ClassicFormProps) => {
    const [hydrated, setHydrated] = useState(false);
    useEffect(() => {
        setHydrated(true);
    }, []);

    const defaultFormValues = useDefaultFormValues({ isEditForm });
    // We expect creating initial form values only once
    // Also with deferred loading at this point
    // we are guaranteed to get all the data from the queries
    const initialFormValuesRef = useRef(defaultFormValues);

    const [getShouldOpenSearchAssist, openSearchAssist] = useSearchAssist();

    const formRef = useRef<FormAPI<FormValues, SubmitMeta>>(null);
    const matchAllTermsRef = useRef<MatchAllTermsProviderRef>(null);

    const handleSubmit = useOnSubmit({ isEditForm });

    useRestoreFormHistoryState({
        enabled: !isEditForm,
        setValues: (values) => formRef.current?.setValues(values),
    });

    const onSubmit = useCallback(
        async (values: FormValues, meta?: SubmitMeta) => {
            if (!isEditForm && getShouldOpenSearchAssist(values)) {
                openSearchAssist();
                return;
            }
            const matchAllTermsExactly = matchAllTermsRef.current?.matchAllTermsExactly ?? false;
            const preferences: { matchAllTermsExactly: boolean; isSkipAssistPlacesModalPermanently?: boolean } = {
                matchAllTermsExactly,
            };
            if (meta?.dontShowSearchAssistAgain) {
                preferences.isSkipAssistPlacesModalPermanently = true;
            }
            await handleSubmit(values, preferences);
        },
        [isEditForm, getShouldOpenSearchAssist, handleSubmit, openSearchAssist],
    );

    useResetFormOnUpstreamChange(formRef, defaultFormValues);

    useOnAddField(({ fieldName, type, newItemIndex, mappedAssertion }) => {
        const formApi = formRef.current;
        invariant(formApi, "Form ref is not initialized");
        const matchAllTermsExactly = matchAllTermsRef.current?.matchAllTermsExactly ?? false;
        const matchAllTermsSupported = matchAllTermsRef.current?.matchAllTermsSupported ?? false;
        const setValueAsExact = matchAllTermsSupported && matchAllTermsExactly;
        if (type === "event") {
            const input = cloneDeep(EVENT_DEFAULT_INSTANCE);
            if (setValueAsExact) {
                input.dateProximity.year = 0;
                input.locationExactness = LOCATION_EXACTNESS_SCOPE.ThisPlace;
            }
            // @ts-expect-error TS can't resolve FieldValue for deeply nested array paths
            formRef.current.appendArrayItem(fieldName as FieldPath<FormValues>, input);
        } else {
            const input = cloneDeep(RELATION_DEFAULT_INSTANCE);
            if (setValueAsExact) {
                input.givenNameExactness.isExact = true;
                input.surnameExactness.isExact = true;
            }
            formRef.current.appendArrayItem(fieldName as FieldPath<FormValues>, input);
        }
        const focusPath = `${fieldName}.${newItemIndex}.${mappedAssertion}` as FieldPath<FormValues>;
        requestAnimationFrame(() => {
            formApi.setFocus(focusPath);
        });
    });

    useOnRemoveField(({ fieldName, index }) => {
        const formApi = formRef.current;
        invariant(formApi, "Form ref is not initialized");
        formApi.removeArrayItem(fieldName as FieldPath<FormValues>, index);
    });

    return (
        <Form
            className={classNames("formLarge480", styles.container, { loading: !hydrated })}
            ref={formRef}
            defaultValues={initialFormValuesRef.current}
            onSubmit={onSubmit}
            data-sfs-hydrated={hydrated ? "true" : "false"}
            inert={hydrated ? undefined : ""}
            aria-busy={hydrated ? undefined : true}
        >
            <MatchAllTermsProvider ref={matchAllTermsRef}>
                <IsEditFormProvider value={isEditForm}>
                    <MemoizedFullFormStructure />
                </IsEditFormProvider>
            </MatchAllTermsProvider>
        </Form>
    );
};
